<script setup lang="ts">
// Repair vs replacement cost ranges as a floating-bar chart (low→high).
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
    BarController,
    BarElement,
    CategoryScale,
    Chart,
    LinearScale,
    Tooltip,
    type ChartConfiguration,
} from 'chart.js'
import { baseScales, chartTheme, chartTooltip, prefersReducedMotion, withAlpha } from '@/shared/charts/theme'
import type { CostRange } from '@/shared/api/types'

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip)

const props = defineProps<{ repair: CostRange; replacement: CostRange }>()

const canvas = ref<HTMLCanvasElement | null>(null)
let chart: Chart<'bar'> | null = null

const usd = (n: number) => `$${n.toLocaleString()}`

function build() {
    if (!canvas.value) return
    chart?.destroy()
    const t = chartTheme()

    const config: ChartConfiguration<'bar', [number, number][]> = {
        type: 'bar',
        data: {
            labels: ['Repair', 'Replace'],
            datasets: [
                {
                    data: [
                        [props.repair.low_usd, props.repair.high_usd],
                        [props.replacement.low_usd, props.replacement.high_usd],
                    ],
                    backgroundColor: [withAlpha(t.accent, 0.85), withAlpha(t.danger, 0.7)],
                    borderRadius: 8,
                    barThickness: 44,
                },
            ],
        },
        options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            animation: prefersReducedMotion() ? false : { duration: 600 },
            scales: {
                x: {
                    ...baseScales(t, 6).y,
                    position: 'bottom',
                    ticks: {
                        color: t.text,
                        font: { family: t.fontMono, size: 10 },
                        callback: (v) => usd(Number(v)),
                    },
                },
                y: {
                    grid: { display: false },
                    border: { color: t.axis },
                    ticks: { color: t.textStrong, font: { family: t.fontBody, size: 13 } },
                },
            },
            plugins: {
                tooltip: {
                    ...chartTooltip(t),
                    callbacks: {
                        label: (ctx) => {
                            const [low, high] = ctx.raw as [number, number]
                            return `${usd(low)} – ${usd(high)}`
                        },
                    },
                },
            },
        },
    }
    chart = new Chart(canvas.value, config)
}

onMounted(build)
watch(() => [props.repair, props.replacement], build, { deep: true })
onBeforeUnmount(() => chart?.destroy())
</script>

<template>
    <div class="cost-chart"><canvas ref="canvas"></canvas></div>
</template>

<style scoped lang="scss">
.cost-chart {
    position: relative;
    height: 180px;
}
</style>
